/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import { formatDate } from '@squiz/dxp-console-support';
import {
  API_TABLE_EVENT_PAGE_NEXT,
  API_TABLE_EVENT_PAGE_PREVIOUS,
  ApiTable,
  ApiTableFetchOptions,
  ApiTablePageTokens,
  ApiTableProps,
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  Button,
  EmptyState,
  ICON_DELETE,
  ICON_FILTER_LIST,
  ICON_SUPERVISED_USER_WITH_MAGNIFYING_GLASS,
  TOOLTIP_POSITION_LEFT,
  TableCell,
  TableContent,
  TextField,
} from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import { useCustomerDelete } from 'apps/customer-data-platform/src/Hooks/UseCustomerDelete/UseCustomerDelete.hook';
import isNull from 'lodash/isNull';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import pick from 'lodash/pick';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import {
  EntityScvCustomer,
  EntityScvCustomers,
  EntityScvCustomersLinks,
  SingleCustomerViewServiceGetCustomersDto,
  getScvService,
} from '../../../../Services/single-customer-view';
import { CUSTOMER_LIST_EMPTY_VALUE } from '../../../SegmentInsights/elements/CustomerList/CustomerList.constants';
import { CustomerSearchProps } from './CustomerSearch.props';

export const decodeSearchParams = (params: URLSearchParams): Record<string, unknown> => {
  const decoded: Record<string, unknown> = {};
  for (const [key, value] of params.entries()) {
    if (key === 'searchParams') {
      decoded[key] = value.split(',');
      if (!Array.isArray(decoded[key])) {
        decoded[key] = [decoded[key]];
      }
    } else {
      decoded[key] = value;
    }
  }
  return decoded;
};

export const createSearchParams = (data: Record<string, string | unknown>): Record<string, string> => {
  return Object.entries(data).reduce((prev: Record<string, string>, [key, value]) => {
    if (Array.isArray(value)) {
      prev[key as string] = value.join(`,`);
    } else if (isUndefined(value) || isNull(value)) {
      prev[key as string] = ``;
    } else {
      prev[key as string] = `${value}`;
    }
    return prev;
  }, {});
};

// Extract a page token string from the customer data.
export const getPageToken = (links: EntityScvCustomersLinks, type: 'next' | 'prev'): string | undefined => {
  let param: string;

  if (type === 'next') {
    param = encodeURIComponent('page[after]');
  } else {
    param = encodeURIComponent('page[before]');
  }

  const pageComponent = links[type].split('&').filter((item) => item.indexOf(param) !== -1);
  if (pageComponent.length) {
    return pageComponent[0].split(`${param}=`)[1];
  }

  return;
};

/**
 * View all customers from a chosen segment.
 */
export const CustomerSearch = (props: CustomerSearchProps): JSX.Element => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const apiTableRef = useRef(null);

  // Single Customer View service
  const singleCustomerViewService = getScvService();
  const [customers, setCustomers] = useState<EntityScvCustomers | null>(null);
  const [searchInput, setSearchInput] = useState<string | null>(searchParams.get(`searchValue`));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const canDeleteCustomer = hasPermission(PERMISSIONS.dxpSCV.CUSTOMER_DELETE);

  // Page token object for pagination
  const pageTokens = useMemo((): ApiTablePageTokens => {
    const pageTokens: ApiTablePageTokens = {};

    if (customers && customers.links) {
      pageTokens.pageAfter = getPageToken(customers.links, 'next');
      pageTokens.pageBefore = getPageToken(customers.links, 'prev');
    }

    return pageTokens;
  }, [customers]);

  const searchCustomer = useCallback(
    (
      partialDto: Partial<SingleCustomerViewServiceGetCustomersDto>,
      options?: ApiTableFetchOptions,
      showLoading: boolean = false,
    ) => {
      if (showLoading) {
        setIsLoading(true);
      }

      let pageSize = isNull(searchParams.get('pageSize')) ? 20 : parseFloat(searchParams.get('pageSize') as string);
      let pageBefore: string | undefined;
      let pageAfter: string | undefined;

      if (options && options.pageSize) {
        pageSize = options.pageSize;
      }

      if (options && options.eventType === API_TABLE_EVENT_PAGE_PREVIOUS) {
        pageBefore = pageTokens.pageBefore;
      }

      if (options && options.eventType === API_TABLE_EVENT_PAGE_NEXT) {
        pageAfter = pageTokens.pageAfter;
      }

      const dto: SingleCustomerViewServiceGetCustomersDto = {
        pageAfter,
        pageBefore,
        pageSize,
        ...partialDto,
      };

      const nextSearchParams = new URLSearchParams(
        createSearchParams({
          ...searchParams,
          ...dto,
        }),
      );
      if (isString(pageSize)) {
        pageSize = parseFloat(pageSize);
      }
      setSearchParams(nextSearchParams);

      lastValueFrom(singleCustomerViewService.getCustomers(dto))
        .then((response) => {
          setCustomers(response as EntityScvCustomers);
          setIsLoading(false);
          return true;
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
          // Just show regular empty message
          setCustomers({ data: [] });
          setIsLoading(false);
        });
    },
    [pageTokens.pageAfter, pageTokens.pageBefore, searchParams, setSearchParams, singleCustomerViewService],
  );

  const onDelete = useCallback((): void => {
    const params = decodeSearchParams(searchParams);
    searchCustomer(
      {
        searchValue: searchInput as string,
        ...(pick(params, [`searchParams`]) as unknown as Array<string>),
      },
      pick(params, [`pageAfter`, `pageBefore`, `pageSize`]) as unknown as ApiTableFetchOptions,
    );
  }, [searchCustomer, searchInput, searchParams]);

  const { openDeleteCustomerModal } = useCustomerDelete({
    onDelete,
  });

  const hasProfileAccess = hasPermission(PERMISSIONS.dxpSCV.CUSTOMER_READ);

  // Handle navigation
  const navigate = useNavigate();
  const handleNavigation = useCallback(
    (itemPath: string): void => {
      navigate(itemPath);
    },
    [navigate],
  );

  // Labels
  const labels = useMemo(() => {
    return {
      customerId: t(CDP_STRINGS.customerId),
      customerSearchNoResult: t(CDP_STRINGS.customerSearchNoResult),
      customerSearchPlaceholder: t(CDP_STRINGS.customerSearchPlaceholder),
      delete: t(CDP_STRINGS.delete),
      deleteCustomer: t(CDP_STRINGS.deleteCustomer),
      email: t(CDP_STRINGS.email),
      lastUpdated: t(CDP_STRINGS.lastUpdated),
      name: t(CDP_STRINGS.name),
      noCustomers: t(CDP_STRINGS.noCustomersCaption),
      search: t(CDP_STRINGS.search),
    };
  }, [t]);

  const fetchData = useCallback(
    (options: ApiTableFetchOptions): void => {
      if (searchInput) {
        searchCustomer(
          {
            searchParams: ['contains'],
            searchValue: searchInput,
          },
          options,
        );
      }
    },
    [searchCustomer, searchInput],
  );

  const tableColumns: Array<TableCell> = useMemo((): Array<TableCell> => {
    const cols = [
      {
        content: labels.customerId,
        key: `customerId`,
      },
      {
        content: labels.name,
        key: `name`,
      },
      {
        content: labels.email,
        key: `email`,
      },
      {
        content: labels.lastUpdated,
        key: `lastUpdated`,
      },
    ];

    if (canDeleteCustomer) {
      cols.push({
        content: ``,
        key: `delete`,
      });
    }
    return cols;
  }, [canDeleteCustomer, labels.customerId, labels.email, labels.lastUpdated, labels.name]);

  const clearSearch = useCallback((): void => {
    // Clear search.
    setSearchInput(null);
    setCustomers(null);
    setSearchParams({});
  }, [setSearchParams]);

  const doSearch = useCallback(
    (input: string | null): void => {
      if (input && /^\s*$/.test(input) === false) {
        searchCustomer(
          {
            searchParams: ['contains'],
            searchValue: input,
          },
          undefined,
          true,
        );
        setSearchInput(input);
      } else {
        clearSearch();
      }
    },
    [clearSearch, searchCustomer],
  );

  useEffect((): void => {
    // If there are no customers loaded and no search input value then check the
    // search params for any initial search data.
    if (!customers && searchInput && searchParams.get(`searchValue`)) {
      const params = decodeSearchParams(searchParams);
      searchCustomer(
        {
          searchValue: searchInput,
          ...(pick(params, [`searchParams`]) as unknown as Array<string>),
        },
        pick(params, [`pageAfter`, `pageBefore`, `pageSize`]) as unknown as ApiTableFetchOptions,
      );
    }
  }, [customers, searchCustomer, searchInput, searchParams]);

  const tableRows = useMemo(() => {
    if (!customers) {
      return [];
    }

    return customers.data.map((customer: EntityScvCustomer) => {
      const { $customerid, $updatedDatetime, $verifiedEmail, firstName, lastName } = customer;

      let fullName = ``;

      if (firstName && lastName) {
        fullName = `${firstName} ${lastName}`;
      } else if (firstName) {
        fullName = firstName;
      } else if (lastName) {
        fullName = lastName;
      }

      const goToCustomerProfile = (event: React.MouseEvent): void => {
        event.preventDefault();
        const customerProfilePath = getCapabilityPath(`/customers/${$customerid}?${searchParams.toString()}`);
        handleNavigation(customerProfilePath);
      };

      const customerLink = hasProfileAccess ? (
        <button
          className="customer-list__id customer-list__link"
          data-id="customer-search-profile-button"
          onClick={goToCustomerProfile}
        >
          {$customerid}
        </button>
      ) : (
        <span className="customer-list__id">{$customerid}</span>
      );

      return {
        customerId: customerLink,
        delete: (
          <Button
            format={BUTTON_FORMAT_TRANSPARENT}
            icon={ICON_DELETE}
            label={labels.delete}
            onClick={(): void => {
              openDeleteCustomerModal($customerid, fullName);
            }}
            theme={BUTTON_THEME_DEFAULT}
            tooltip={{
              label: labels.deleteCustomer,
              position: TOOLTIP_POSITION_LEFT,
            }}
          />
        ),
        email: !$verifiedEmail || /^\s*$/.test($verifiedEmail) ? CUSTOMER_LIST_EMPTY_VALUE : $verifiedEmail,
        lastUpdated: $updatedDatetime ? formatDate(`${$updatedDatetime}`) : CUSTOMER_LIST_EMPTY_VALUE,
        name: fullName,
      };
    });
  }, [
    customers,
    handleNavigation,
    hasProfileAccess,
    labels.delete,
    labels.deleteCustomer,
    openDeleteCustomerModal,
    searchParams,
  ]);

  const tableContent: TableContent = { columns: tableColumns, rows: tableRows };
  const tableProps: ApiTableProps = {
    fetchData,
    initialFetch: false,
    loading: isLoading,
    pageTokens,
    perPageDefault: parseFloat(searchParams.get(`pageSize`) ? (searchParams.get(`pageSize`) as string) : `20`) || 20,
    tableContent: tableContent,
  };

  const canSearch = useMemo((): boolean => {
    return searchInput !== null && /[a-z0-9\-_@]{2,}/.test(searchInput);
  }, [searchInput]);

  return (
    <div className="customer-search__wrapper">
      <form action="javascript:void(0);" className="customer-search__form">
        <div className="customer-search__form-input">
          <TextField
            fieldLabel="Customers"
            hideLabel
            icon={ICON_FILTER_LIST}
            id="customer-search-input"
            name="customer-search-input"
            onChange={(event): void => {
              const inputValue = (event.target as HTMLInputElement).value;
              setSearchInput(inputValue);
            }}
            onKeyUp={(event): void => {
              if (event.code === `Enter`) {
                doSearch(searchInput);
              }
            }}
            placeholder={labels.customerSearchPlaceholder}
            value={searchInput || ``}
          />
        </div>
        <div className="customer-search__form-button">
          <Button
            format={BUTTON_FORMAT_SOFT}
            id="customer-search-button"
            isDisabled={canSearch === false}
            isLoading={isLoading}
            label={labels.search}
            onClick={(): void => {
              doSearch(searchInput);
            }}
            readOnly={canSearch === false}
            text={labels.search}
            theme={BUTTON_THEME_DEFAULT}
          />
        </div>
      </form>
      {customers === null && (
        <EmptyState
          caption={labels.customerSearchPlaceholder}
          isLite={false}
          svg={ICON_SUPERVISED_USER_WITH_MAGNIFYING_GLASS}
        />
      )}
      {customers !== null && customers.data.length === 0 && (
        <EmptyState
          caption={labels.customerSearchNoResult}
          isLite={false}
          svg={ICON_SUPERVISED_USER_WITH_MAGNIFYING_GLASS}
        />
      )}
      {customers !== null && customers.data.length >= 1 && <ApiTable {...tableProps} ref={apiTableRef} />}
    </div>
  );
};
